/**
 * Surveillance automatique du canal WhatsApp, en phrases simples.
 *
 * Le serveur surveille tout seul la session WhatsApp, l'adresse où les
 * messages arrivent, la file d'envoi et un message témoin du matin; il
 * renvoie ses constats dans `watchdog` (réponse de la santé de l'envoi).
 * Ce module transforme ces constats en lignes prêtes à afficher: aucun code,
 * aucune date brute, aucun mot anglais.
 *
 * `resumeSurveillance` est pur (l'heure est un paramètre): il se teste sans
 * écran. Il renvoie `null` quand le serveur ne fournit pas encore `watchdog`.
 */

import { libelle } from './libelles';
import {
  accorde,
  enDate,
  formatDate,
  formatDateHeure,
  formatDuree,
  formatHeureParlee,
  formatIlYA,
} from './formats';

const DOMAINE = 'surveillanceWhatsapp';

/** États d'une session que WhatsApp considère comme prête à envoyer et recevoir. */
export const SESSIONS_SAINES = ['connected', 'ready', 'authenticated', 'working'];

/** Nombre de relances automatiques que le serveur tente avant de demander une intervention. */
export const RELANCES_MAX = 3;

/** Un message qui attend depuis plus longtemps n'est plus « en cours d'envoi »: il bloque. */
export const ATTENTE_LONGUE_MS = 15 * 60 * 1000;

/** Une surveillance qui n'a pas vérifié depuis ce temps est elle-même à vérifier. */
export const VERIFICATION_PERIMEE_MS = 5 * 60 * 1000;

const L = (code) => libelle(DOMAINE, code);

const majuscule = (texte) => (texte ? texte.charAt(0).toUpperCase() + texte.slice(1) : texte);

const estObjet = (valeur) => Boolean(valeur) && typeof valeur === 'object' && !Array.isArray(valeur);

const texteOuNull = (valeur) => {
  if (typeof valeur !== 'string') return null;
  const texte = valeur.trim();
  // « null » ou « undefined » écrits en toutes lettres sont un champ vide mal sérialisé.
  return texte && !/^(?:null|undefined|nan)$/i.test(texte) ? texte : null;
};

/** Un nombre entier positif (même reçu en texte), sinon `null`: jamais « NaN » à l'écran. */
const entierOuNull = (valeur) => {
  const lisible = typeof valeur === 'number' || (typeof valeur === 'string' && valeur.trim() !== '');
  const nombre = lisible ? Number(valeur) : NaN;
  return Number.isFinite(nombre) ? Math.max(0, Math.floor(nombre)) : null;
};

/** Temps écoulé depuis une date, en millisecondes; `null` si la date manque. */
const ecoule = (valeur, maintenant) => {
  const date = enDate(valeur);
  return date ? Math.max(0, maintenant - date.getTime()) : null;
};

/** « ce matin », « hier », « le 13/10/2026 »: le jour d'un événement, dit comme on le dit. */
const quand = (date, maintenant) => {
  const jour = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const ecart = Math.round((jour(new Date(maintenant)) - jour(date)) / 86400000);
  if (ecart <= 0) return date.getHours() < 12 ? 'ce matin' : 'aujourd’hui';
  if (ecart === 1) return 'hier';
  return `le ${formatDate(date)}`;
};

/** « ce matin à 6 h 01 » */
const quandEtHeure = (valeur, maintenant) => {
  const date = enDate(valeur);
  return date ? `${quand(date, maintenant)} à ${formatHeureParlee(date)}` : null;
};

const ligneSession = (w, maintenant) => {
  const statut = String(w.session_status || '').trim().toLowerCase();
  const coupeeDepuis = ecoule(w.session_down_since, maintenant);
  const saine = SESSIONS_SAINES.includes(statut);
  const tentatives = entierOuNull(w.restart_attempts);
  const derniereRelance = enDate(w.last_restart_at)
    ? formatIlYA(w.last_restart_at, null, maintenant)
    : null;
  const intitule = L('ligne_session');

  if (saine && !w.session_down_since) {
    return {
      cle: 'session',
      libelle: intitule,
      etat: { code: 'session_connectee', texte: L('session_connectee') },
      details: derniereRelance ? [{ texte: `Relancée automatiquement ${derniereRelance}` }] : [],
    };
  }
  if (!saine && !w.session_down_since && (!statut || statut === 'not_checked')) {
    return {
      cle: 'session',
      libelle: intitule,
      etat: { code: 'session_inconnue', texte: L('session_inconnue') },
      details: [],
    };
  }

  let texte = L('session_coupee');
  if (coupeeDepuis !== null) texte += ` depuis ${formatDuree(coupeeDepuis)}`;
  if (tentatives !== null) {
    texte +=
      tentatives <= RELANCES_MAX
        ? ` · ${L('relance')} ${tentatives}/${RELANCES_MAX}`
        : ` · ${accorde(tentatives, 'relance automatique', 'relances automatiques')}`;
  }
  const details = [];
  if (tentatives !== null && tentatives >= RELANCES_MAX) details.push({ texte: L('relance_epuisee') });
  else if (derniereRelance) details.push({ texte: `Dernière relance ${derniereRelance}` });
  return {
    cle: 'session',
    libelle: intitule,
    etat: { code: 'session_coupee', texte },
    details,
  };
};

const ligneReception = (w, maintenant) => {
  const adresse = estObjet(w.webhook) ? w.webhook : null;
  const attendue = texteOuNull(adresse?.expected_url);
  const enregistree = texteOuNull(adresse?.registered_url);

  let code = 'adresse_inconnue';
  if (adresse?.ok === false) code = 'adresse_incorrecte';
  else if (adresse?.ok === true) {
    code = enregistree && attendue && enregistree !== attendue ? 'adresse_corrigee' : 'adresse_ok';
  }

  const details = [];
  if (attendue) details.push({ intitule: L('adresse_attendue'), texte: attendue, selectionnable: true });
  if (code === 'adresse_incorrecte') {
    details.push({
      intitule: L('adresse_enregistree'),
      texte: enregistree || L('adresse_aucune'),
      selectionnable: Boolean(enregistree),
    });
  }
  if (adresse && enDate(adresse.checked_at)) {
    details.push({ texte: `Vérifiée ${formatIlYA(adresse.checked_at, null, maintenant)}` });
  }
  return {
    cle: 'reception',
    libelle: L('ligne_reception'),
    etat: { code, texte: L(code) },
    details,
  };
};

/** Une ligne « date seule »: « Il y a 3 min » ou « Aucun », la date exacte dessous. */
const ligneHorodatee = (cle, intitule, valeur, maintenant, { videDetail, seuilMs } = {}) => {
  const depuis = ecoule(valeur, maintenant);
  if (depuis === null) {
    return {
      cle,
      libelle: intitule,
      valeur: L('aucun'),
      vide: true,
      details: videDetail ? [{ texte: videDetail }] : [],
    };
  }
  const ligne = {
    cle,
    libelle: intitule,
    valeur: majuscule(formatIlYA(valeur, '', maintenant)),
    vide: false,
    details: [{ texte: formatDateHeure(valeur) }],
  };
  if (seuilMs && depuis >= seuilMs) ligne.attention = true;
  return ligne;
};

const ligneTemoin = (w, maintenant) => {
  const intitule = L('ligne_temoin');
  const temoin = estObjet(w.canary) ? w.canary : null;
  if (!temoin || temoin.enabled !== true) {
    return {
      cle: 'temoin',
      libelle: intitule,
      etat: { code: 'temoin_desactive', texte: L('temoin_desactive') },
      details: [],
    };
  }
  if (temoin.ok === true) {
    const recu = quandEtHeure(temoin.last_delivered_at, maintenant);
    return {
      cle: 'temoin',
      libelle: intitule,
      etat: { code: 'temoin_recu', texte: recu ? `${L('temoin_recu')} ${recu}` : L('temoin_recu') },
      details: [],
    };
  }
  const envoi = quandEtHeure(temoin.last_sent_at, maintenant);
  const details = envoi ? [{ texte: `${L('temoin_envoye')} ${envoi}` }] : [];
  if (temoin.ok === false) {
    return {
      cle: 'temoin',
      libelle: intitule,
      etat: { code: 'temoin_manque', texte: L('temoin_manque') },
      details,
    };
  }
  return {
    cle: 'temoin',
    libelle: intitule,
    etat: { code: 'temoin_attendu', texte: L('temoin_attendu') },
    details,
  };
};

/**
 * Les lignes de la surveillance, ou `null` si le serveur n'envoie pas `watchdog`.
 *
 * Une ligne a soit un `etat` (pastille: `code` pour la teinte, `texte` pour
 * l'écran), soit une `valeur` (texte simple, `vide` si « Aucun », `attention`
 * si elle mérite l'œil); `details` sont les petites lignes dessous.
 */
export const resumeSurveillance = (watchdog, maintenant = Date.now()) => {
  if (!estObjet(watchdog)) return null;
  const w = watchdog;
  const depuisVerification = ecoule(w.checked_at, maintenant);
  const attente = ligneHorodatee('attente', L('ligne_attente'), w.oldest_pending_at, maintenant, {
    videDetail: L('attente_vide'),
    seuilMs: ATTENTE_LONGUE_MS,
  });
  return {
    verification: {
      libelle: L('verification'),
      texte: depuisVerification === null ? L('verification_jamais') : formatIlYA(w.checked_at, '', maintenant),
      perimee: depuisVerification !== null && depuisVerification >= VERIFICATION_PERIMEE_MS,
    },
    lignes: [
      ligneSession(w, maintenant),
      ligneReception(w, maintenant),
      ligneHorodatee('accuse', L('ligne_accuse'), w.last_ack_at, maintenant),
      ligneHorodatee('recu', L('ligne_recu'), w.last_inbound_at, maintenant),
      attente,
      ligneTemoin(w, maintenant),
    ],
  };
};
